/**
 * The tenant's national identifier types (BVN, NIN, ...) come from
 * franchisees/tenant-and-country as tenantCountry.nationalIdentifiers, but not
 * always as a clean string[]. A tenant with none configured has come back as a
 * serialised empty list, which the identifier-type selects rendered verbatim as
 * an option labelled "[]" — and pre-selected it, so it was saved on clients too.
 *
 * Every consumer goes through nationalIdentifierOptions() so the shape is
 * handled once: arrays, JSON strings and comma-separated strings are accepted,
 * placeholders are dropped, and the list is never empty.
 */

/** Values that mean "nothing configured" rather than a real identifier type. */
const PLACEHOLDERS = new Set(['', '[]', '{}', 'null', 'undefined']);

/** Fallback used across the app when a tenant has no identifier configured. */
export const DEFAULT_NATIONAL_IDENTIFIER = 'BVN';

/**
 * A single identifier type as a clean string, or null when the value is a
 * placeholder ("[]", "null", "") or not a string at all.
 */
export function cleanIdentifierType(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const cleaned = value
    .trim()
    .replace(/^[\s"'[\]]+|[\s"'[\]]+$/g, '')
    .trim();
  return PLACEHOLDERS.has(cleaned.toLowerCase()) ? null : cleaned;
}

/** Every real identifier type in `raw`, in order, without duplicates. */
export function parseNationalIdentifiers(raw: unknown): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  const add = (value: unknown) => {
    const id = cleanIdentifierType(value);
    if (id && !seen.has(id.toUpperCase())) {
      seen.add(id.toUpperCase());
      result.push(id);
    }
  };

  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        visit(JSON.parse(trimmed));
        return;
      } catch {
        // Not JSON (e.g. "[BVN, NIN]") — fall through and split it.
      }
    }
    trimmed.split(',').forEach(add);
  };

  visit(raw);
  return result;
}

/**
 * The identifier types to offer for this tenant. Never empty: falls back to
 * the tenant's financial identifier acronym, then to BVN, matching the
 * default the rest of the app already assumes.
 */
export function nationalIdentifierOptions(tenantCountry: any): string[] {
  const ids = parseNationalIdentifiers(tenantCountry?.nationalIdentifiers);
  if (ids.length) return ids;
  return [
    cleanIdentifierType(tenantCountry?.financialIdentifierAcronym) ||
      DEFAULT_NATIONAL_IDENTIFIER,
  ];
}
